import { MoreOutlined } from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import CommonHeader from "../../components/elements/CommonHeader";
import DatePickerWithIcon from "../../components/elements/DatePickerWithIcon";
import ROUTE_PATHS from "../../utils/routePaths";
import useLandingAuthApi from "../../services/api/useLandingAuthApi";
import PaginationComponent from "../ListOfTripRequest/views/PaginationComponent";
import SnackbarAlert from "../../components/elements/SnackbarAlert";
import "./NotificationsScreen.css";
import { useFormData } from "../../helper/FormDataProvider";
import notificationIcon from "../../assets/images/notification_icon.svg";
import moment from "moment";
import { useError } from "../../services/ErrorProvider";

const NotificationsScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useFormData();
  const { showError } = useError();
  const {
    fetchInAppNotification,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
    fetchUnreadNotificationCount,
    fetchUnreadNotifications,
  } = useLandingAuthApi();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [activeMenu, setActiveMenu] = useState(null);
  const [page, setPage] = useState(() => {
    return location?.state?.currentPage || 1;
  });
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [markAllLoading, setMarkAllLoading] = useState(false);
  const [markAllSuccessMessage, setMarkAllSuccessMessage] = useState("");
  const [markAllErrorMessage, setMarkAllErrorMessage] = useState("");
  const [notificationActionInProgress, setNotificationActionInProgress] = useState(
    {}
  );

  const UNREAD_NOTIFICATION_COUNT_STORAGE_KEY = "unreadNotificationCount";

  const formatDateTime = (dateTimeString) => {
    return moment(dateTimeString).format("MMM D, YYYY HH:mm");
  };

  const loadNotifications = async ({ showLoader = true } = {}) => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      const response = await fetchInAppNotification(page - 1, size);

      if (response?.data) {
        const notificationsData = response.data.content.map((notification) => ({
          id: notification.id,
          message: notification.notification,
          read: notification.isRead,
          itemId: notification.itemId,
          serviceType: notification.serviceType,
          createdAt: notification.createdAt,
        }));
        setNotifications(notificationsData);
        setTotalPages(response.data.totalPages);

        if (page > response.data.totalPages) {
          setPage(1);
        }
      }
    } catch (error) {
       if (error?.status === 404) {
        // Backend returns 404 when no notifications exist for the profile.
        setNotifications([]);
        setTotalPages(1);
      } else {
        showError(error?.message || "Failed to fetch notifications");
      }
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (userData?.data?.id) {
      loadNotifications();
    }
  }, [userData?.data?.id, page, size, filter]);

  useEffect(() => {
    if (location?.state?.currentPage) {
      setPage(location.state.currentPage);
    }
  }, [location?.state?.currentPage]);
  
  const handleMarkAsRead = async (notificationId) => {
    if (notificationActionInProgress[notificationId]) return;
    setNotificationActionInProgress((prev) => ({
      ...prev,
      [notificationId]: "read",
    }));

    try {
      await markNotificationAsRead(notificationId);
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
      showError(error);
      loadNotifications({ showLoader: false });
    } finally {
      setNotificationActionInProgress((prev) => {
        const next = { ...prev };
        delete next[notificationId];
        return next;
      });
      setActiveMenu(null);
    }
  };

  const handleMarkAsUnread = async (notificationId) => {
    if (notificationActionInProgress[notificationId]) return;
    setNotificationActionInProgress((prev) => ({
      ...prev,
      [notificationId]: "unread",
    }));

    try {
      await fetchUnreadNotifications(notificationId);
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: false }
            : notification
        )
      );
    } catch (error) {
      console.error("Error marking notification as unread:", error);
      showError(error);
      loadNotifications({ showLoader: false });
    } finally {
      setNotificationActionInProgress((prev) => {
        const next = { ...prev };
        delete next[notificationId];
        return next;
      });
      setActiveMenu(null);
    }
  };

  const handleDelete = async (notificationId) => {
    if (notificationActionInProgress[notificationId]) return;
    setNotificationActionInProgress((prev) => ({
      ...prev,
      [notificationId]: "delete",
    }));

    try {
      await deleteNotification(notificationId);

      if (notifications.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        loadNotifications({ showLoader: false });
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
      showError(error);
      loadNotifications({ showLoader: false });
    } finally {
      setNotificationActionInProgress((prev) => {
        const next = { ...prev };
        delete next[notificationId];
        return next;
      });
      setActiveMenu(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setMarkAllLoading(true);
      setMarkAllErrorMessage("");
      setMarkAllSuccessMessage("");

      const markAllResponse = await markAllNotificationsAsRead();

      // Update currently visible items immediately; other pages will be correct once re-fetched.
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) => ({
          ...notification,
          read: true,
        }))
      );
      setActiveMenu(null);

      // Refresh unread count so badge updates elsewhere.
      try {
        const unreadCountResponse = await fetchUnreadNotificationCount(
          userData?.data?.id
        );
        const newUnreadCount = unreadCountResponse?.data;
        if (typeof newUnreadCount === "number") {
          localStorage.setItem(
            UNREAD_NOTIFICATION_COUNT_STORAGE_KEY,
            String(newUnreadCount)
          );
        }
      } catch (countError) {
        console.error("Error refreshing unread notification count:", countError);
      }

      const possibleMessage = markAllResponse?.data ?? markAllResponse;
      const successMessage =
        typeof possibleMessage === "string"
          ? possibleMessage
          : "All notifications marked as read";

      setMarkAllSuccessMessage(successMessage);
      setTimeout(() => setMarkAllSuccessMessage(""), 6000);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      const message = error?.message || "Failed to mark notifications as read";
      setMarkAllErrorMessage(message);
      setTimeout(() => setMarkAllErrorMessage(""), 6000);
    } finally {
      setMarkAllLoading(false);
    }
  };

  const getFilteredNotifications = () => {
    if (filter === "all") return notifications;
    if (filter === "read")
      return notifications.filter((notification) => notification.read);
    if (filter === "unread")
      return notifications.filter((notification) => !notification.read);
    return notifications;
  };

  const toggleMenu = (index) => {
    setActiveMenu(activeMenu === index ? null : index);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      // Delegate to the same guarded handler used by the menu item.
      await handleMarkAsRead(notification.id);
    }

    if (notification.serviceType === "TRIP" && notification.itemId) {
      navigate(`${ROUTE_PATHS.TRIP_DETAILS(notification.itemId)}`, {
        state: {
          fromScreen: "notifications",
          currentPage: page,
          tripId: notification.itemId,
        },
      });
    }
  };

  const handleFilterChange = (filterType) => {
    if (filter === filterType) {
      setFilter("all");
    } else {
      setFilter(filterType);
    }
    setActiveMenu(null);
  };

  const filteredNotifications = getFilteredNotifications();

  return (
    <div className="notification-screen-container">
      <CommonHeader
        headerText="Notifications"
        centerLogoSrc={notificationIcon}
        backContent={
          <div className="navigation-header">
            <Link
              to={ROUTE_PATHS.TRIP_REQUEST_PAGE}
              className="navigation-header-link"
            >
              Home/
            </Link>
            Notifications
          </div>
        }
      />

      {markAllSuccessMessage && (
        <SnackbarAlert type="success">{markAllSuccessMessage}</SnackbarAlert>
      )}
      {markAllErrorMessage && (
        <SnackbarAlert type="error">{markAllErrorMessage}</SnackbarAlert>
      )}

      <div className="notification-screen-filters">
        <div className="notification-screen-filters-row">
          <div className="notification-screen-filter-options">
            <label className="notification-screen-checkbox-container">
              <input
                type="radio"
                name="filter"
                checked={filter === "all"}
                onChange={() => handleFilterChange("all")}
              />
              All
            </label>
            <label className="notification-screen-checkbox-container">
              <input
                type="radio"
                name="filter"
                checked={filter === "read"}
                onChange={() => handleFilterChange("read")}
              />
              Read
            </label>
            <label className="notification-screen-checkbox-container">
              <input
                type="radio"
                name="filter"
                checked={filter === "unread"}
                onChange={() => handleFilterChange("unread")}
              />
              Unread
            </label>
          </div>

          <div className="notification-screen-mark-all">
            <button
              type="button"
              className="notification-mark-all-button"
              onClick={handleMarkAllAsRead}
              disabled={markAllLoading || notifications.length === 0}
              data-testid="mark-all-as-read-button"
            >
              {markAllLoading ? (
                <>
                  Marking...
                  <span
                    className="notification-mark-all-button-spinner"
                    aria-hidden="true"
                  />
                </>
              ) : (
                "Mark All as Read"
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="notification-screen-list">
        {loading && notifications.length === 0 ? (
          <div className="notification-loading">Loading notifications...</div>
        ) : (
          <>
            {filteredNotifications.length === 0 ? (
              <div className="notification-empty">No notifications found</div>
            ) : (
              filteredNotifications.map((notification, index) => (
                <div
                  key={notification.id}
                  className={`notification-screen-item ${notification.read ? "" : "notification-unread"}`}
                >
                  <div
                    className="notification-screen-content"
                    onClick={() => handleNotificationClick(notification)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-timestamp">
                      {formatDateTime(notification.createdAt)}
                    </div>
                  </div>
                  <div className="notification-screen-actions">
                    <div className="notification-screen-menu">
                      {/*
                        Only one action per notification can run at a time.
                        While running, disable further actions and show status text.
                      */}
                      {(() => {
                        const currentAction =
                          notificationActionInProgress[notification.id];
                        const actionInProgress = Boolean(currentAction);
                        return (
                          <>
                      <button
                        className="notification-screen-menu-button"
                        data-testid="notification-menu-button"
                        onClick={() => toggleMenu(index)}
                        disabled={actionInProgress}
                      >
                        <MoreOutlined rotate={90} />
                      </button>
                      {activeMenu === index && (
                        <div className="notification-screen-dropdown">
                          {!notification.read && (
                            <div
                              className="notification-screen-menu-item"
                              onClick={() =>
                                !actionInProgress &&
                                handleMarkAsRead(notification.id)
                              }
                              style={{
                                opacity: actionInProgress ? 0.6 : 1,
                                pointerEvents: actionInProgress
                                  ? "none"
                                  : "auto",
                                cursor: actionInProgress
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                            >
                              {currentAction === "read"
                                ? "Marking as Read..."
                                : "Mark as Read"}
                            </div>
                          )}
                          {notification.read && (
                            <div
                              className="notification-screen-menu-item"
                              onClick={() =>
                                !actionInProgress &&
                                handleMarkAsUnread(notification.id)
                              }
                              style={{
                                opacity: actionInProgress ? 0.6 : 1,
                                pointerEvents: actionInProgress
                                  ? "none"
                                  : "auto",
                                cursor: actionInProgress
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                            >
                              {currentAction === "unread"
                                ? "Marking as Unread..."
                                : "Mark as Unread"}
                            </div>
                          )}
                          <div
                            className="notification-screen-menu-item"
                            onClick={() =>
                              !actionInProgress && handleDelete(notification.id)
                            }
                            style={{
                              opacity: actionInProgress ? 0.6 : 1,
                              pointerEvents: actionInProgress ? "none" : "auto",
                              cursor: actionInProgress ? "not-allowed" : "pointer",
                            }}
                          >
                            {currentAction === "delete" ? "Deleting..." : "Delete"}
                          </div>
                        </div>
                      )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))
            )}
            {filteredNotifications.length > 0 && totalPages > 1 && (
              <div className="pagination-container">
                <PaginationComponent
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={(newPage) => {
                    setPage(newPage);
                    setActiveMenu(null);
                    window.scrollTo(0, 0);
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsScreen;
